import { OrderType } from '../schemas';
import { DaiCostsField, SettingsDaiCostsResponse } from './types';

type DaiCostsRow = SettingsDaiCostsResponse['data'][number];

/**
 * sortString / sortNumber と同様、欠損表示（'-', null, 空文字）かどうか
 */
const isMissingStringValue = (value: string | null | undefined): boolean =>
  value == null || value === '' || value === '-';

const isMissingNumberValue = (value: number | null | undefined): boolean =>
  value == null;

const compareStringValue = (a: string, b: string): number =>
  a.localeCompare(b, ['en', 'ja'], {
    sensitivity: 'base',
    ignorePunctuation: true,
    numeric: true,
  });

/**
 * 欠損値は昇順・降順に関わらず常に末尾へ送る
 */
const compareOrdered = (
  aMissing: boolean,
  bMissing: boolean,
  compareNonMissing: () => number,
  order: OrderType
): number => {
  if (aMissing && bMissing) {
    return 0;
  }
  if (aMissing) {
    return 1;
  }
  if (bMissing) {
    return -1;
  }

  const comparison = compareNonMissing();
  return order === 'asc' ? comparison : -comparison;
};

const compareStringField = (
  a: string | null | undefined,
  b: string | null | undefined,
  order: OrderType
): number =>
  compareOrdered(
    isMissingStringValue(a),
    isMissingStringValue(b),
    () => compareStringValue(a ?? '', b ?? ''),
    order
  );

const compareNumberField = (
  a: number | null | undefined,
  b: number | null | undefined,
  order: OrderType
): number =>
  compareOrdered(
    isMissingNumberValue(a),
    isMissingNumberValue(b),
    () => (a ?? 0) - (b ?? 0),
    order
  );

const compareByField = (
  a: DaiCostsRow,
  b: DaiCostsRow,
  sort: DaiCostsField,
  order: OrderType
): number => {
  switch (sort) {
    case 'initialHallName':
    case 'currentHallName':
    case 'mainBoardNo':
    case 'kiMei':
    case 'konyuKbn':
      return compareStringField(a[sort], b[sort], order);
    case 'donyuDay':
      return compareStringField(a.donyuDay, b.donyuDay, order);
    case 'konyuGaku':
    case 'syoruiGaku':
    case 'saleGaku':
      return compareNumberField(a[sort], b[sort], order);
    case 'saleDay':
      return compareStringField(a.saleDay, b.saleDay, order);
    default:
      return 0;
  }
};

export const sortDaiCostsRow = (
  a: DaiCostsRow,
  b: DaiCostsRow,
  sort: DaiCostsField,
  order: OrderType
): number => compareByField(a, b, sort, order);

export const sortDaiCosts = (
  dataDaiCosts: SettingsDaiCostsResponse,
  order: OrderType,
  sort: DaiCostsField
): SettingsDaiCostsResponse => {
  if (!dataDaiCosts) {
    return dataDaiCosts;
  }

  const sortedData = [...dataDaiCosts.data].sort((a, b) =>
    sortDaiCostsRow(a, b, sort, order)
  );

  return {
    ...dataDaiCosts,
    data: sortedData,
  };
};
